import { useCallback } from "react";

import { type To, useNavigate } from "@cinnamon/design-system/hooks";

import useTenantAwareNavigationLinkProps, {
  type TenantAwareNavigateOptions,
  type UseTenantAwareNavigationLinkPropsConfig,
} from "./useTenantAwareNavigationLinkProps";

/**
 * Hook that provides tenant-aware navigation.
 * Automatically injects tenantId into all navigation calls (with opt-out).
 *
 * @param config - Configuration object
 * @returns Enhanced navigate function that auto-injects tenant_id
 *
 * @example
 * ```tsx
 * const tenantId = useTenantContext();
 * const navigate = useTenantAwareNavigation({ tenantId });
 *
 * // Automatically includes tenant_id
 * navigate("/workflows");
 * // Result: "/workflows?tenant_id=tenant-123"
 *
 * // Opt-out for specific navigation
 * navigate("/public/page", { skipTenantId: true });
 * // Result: "/public/page" (no tenant_id)
 *
 * // Excluded routes (login, etc.) automatically skip tenant_id
 * navigate("/login");
 * // Result: "/login" (no tenant_id)
 * ```
 */
interface UseTenantAwareNavigationReturn {
  (to: To, options?: TenantAwareNavigateOptions): void;
  (delta: number): void;
}
export const useTenantAwareNavigation = (
  config?: UseTenantAwareNavigationLinkPropsConfig
): UseTenantAwareNavigationReturn => {
  const getLinkProps = useTenantAwareNavigationLinkProps(config);
  const baseNavigate = useNavigate();

  const navigate = useCallback(
    (to: To | number, options?: TenantAwareNavigateOptions): void => {
      if (typeof to === "number") {
        return baseNavigate(to);
      }

      const { to: enhancedTo, ...restOptions } = getLinkProps(to, options);

      return baseNavigate(enhancedTo, restOptions);
    },
    [baseNavigate, getLinkProps]
  );

  return navigate;
};

/**
 * Type for the enhanced navigate function
 */
export type TenantAwareNavigate = ReturnType<typeof useTenantAwareNavigation>;

export default useTenantAwareNavigation;
